import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck, Linkedin, Twitter } from "lucide-react";
import { COMPANY } from "@/lib/site-data";

export default function Footer() {
    const columns = [
        { title: "Solutions", links: [
            { label: "DC Fast Charging", to: "/services#dc-fast" },
            { label: "Megawatt Charging (MCS)", to: "/services#mcs" },
            { label: "Level 2 Charging", to: "/services#level-2" },
            { label: "Marine Charging", to: "/services#marine" },
            { label: "Aviation Charging", to: "/services#aviation" },
            { label: "Fleet Charging", to: "/fleet" },
            { label: "Commercial Property", to: "/commercial" },
        ]},
        { title: "Programs", links: [
            { label: "Site Host Program", to: "/site-host" },
            { label: "Grant Assistance", to: "/grants" },
            { label: "Projects", to: "/projects" },
            { label: "Technology Platform", to: "/technology" },
            { label: "Sustainability", to: "/sustainability" },
        ]},
        { title: "Company", links: [
            { label: "About", to: "/about" },
            { label: "Investor Relations", to: "/investors" },
            { label: "Careers", to: "/careers" },
            { label: "News & Insights", to: "/blog" },
            { label: "Contact", to: "/contact" },
        ]},
    ];
    return (
        <footer data-testid="site-footer" className="relative bg-ink-900 border-t border-white/10 mt-24">
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pt-20 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <img
                                src="https://customer-assets.emergentagent.com/job_charge-everywhere-2/artifacts/ax26z8mx_Logo.png"
                                alt="M-Class EV Charging"
                                className="w-12 h-12 object-contain"
                            />
                            <div>
                                <div className="font-heading font-semibold text-white text-lg">M-Class</div>
                                <div className="text-[10px] text-electric-400 tracking-[0.25em] uppercase">EV Charging</div>
                            </div>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed max-w-sm mb-6">
                            Turnkey EV charging infrastructure for municipalities, commercial property owners, fleets, multifamily developments, and public-private partnerships.
                        </p>
                        <div className="inline-flex items-center gap-2 border border-electric-400/40 bg-electric-400/5 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-electric-400">
                            <ShieldCheck className="w-3.5 h-3.5" /> Veteran-Owned
                        </div>
                    </div>

                    {columns.map((col) => (
                        <div key={col.title} className="md:col-span-2">
                            <div className="eyebrow mb-5">{col.title}</div>
                            <ul className="space-y-3">
                                {col.links.map((l) => (
                                    <li key={l.label}><Link data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-').replace('&','and')}`} to={l.to} className="text-sm text-slate-400 hover:text-electric-400 transition-colors">{l.label}</Link></li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="md:col-span-2">
                        <div className="eyebrow mb-5">Contact</div>
                        <ul className="space-y-3 text-sm text-slate-400">
                            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-electric-400 mt-0.5 shrink-0" />{COMPANY.city}</li>
                            <li className="flex items-start gap-2"><Phone className="w-4 h-4 text-electric-400 mt-0.5 shrink-0" /><a data-testid="footer-phone" href={`tel:${COMPANY.phone}`} className="hover:text-electric-400">{COMPANY.phone}</a></li>
                            <li className="flex items-start gap-2"><Mail className="w-4 h-4 text-electric-400 mt-0.5 shrink-0" /><a data-testid="footer-email" href={`mailto:${COMPANY.email}`} className="hover:text-electric-400 break-all">{COMPANY.email}</a></li>
                        </ul>
                        <div className="flex items-center gap-3 mt-6">
                            <a href="#" data-testid="footer-linkedin" aria-label="LinkedIn" className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-electric-400 hover:text-electric-400 text-slate-400"><Linkedin className="w-4 h-4" /></a>
                            <a href="#" data-testid="footer-twitter" aria-label="Twitter" className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-electric-400 hover:text-electric-400 text-slate-400"><Twitter className="w-4 h-4" /></a>
                        </div>
                    </div>
                </div>

                <div className="section-rule my-10" />

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-500">
                    <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</div>
                    <div className="flex items-center gap-6">
                        <span className="font-mono tracking-wider">{COMPANY.tagline}</span>
                        <Link to="/contact" className="hover:text-electric-400 uppercase tracking-[0.18em]">Privacy</Link>
                        <Link to="/contact" className="hover:text-electric-400 uppercase tracking-[0.18em]">Terms</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
